import React from 'react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'
import { ImageWidths } from 'common/helpers/image'

import {
    StyledPortrait,
    StyledLink,
    StyledCardText,
    StyledCardMedia,
    StyledCardByline,
    StyledSponsoredLabel,
} from 'component-library/cards/Portrait/Portrait.styled'

import { toLinkWithHint } from 'common/routing/route-hints'

import { ThemeProps } from 'component-library/__styling/themes'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'
import { themedValue } from 'component-library/__styling/themed-value'
import { GetVideoQueue } from 'component-library/content/Video/PlayerInterface'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio, CardBreakpointRatios } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import { doesParticularRatioExist } from '../CardMedia/image-helpers'
import { isVideoClick } from 'component-library/__helpers/video-card-helpers'
import { KickerPosition } from '../Kicker/Kicker.styled'
import { withTheme } from '@emotion/react'
import { createCardClickedEvent } from 'common/events/collection-events'
import { PublicationCardItem } from 'common/client-data-types/card-types'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'

export interface CardOrientationOptions {
    type: 'Landscape-Portrait'
    maxBreakpoint?: 'sm' | 'md'
}

export interface PortraitState {
    isPlaying?: boolean
}

export interface Props extends CommonCardProps {
    hasPadding?: boolean
    hasBackground?: boolean
    hasSeparator?: boolean

    mediaMode?: MediaMode
    hideByline?: boolean

    hideTeaser?: boolean

    /// TODO: Combine Kicker Props
    hideKicker?: boolean
    kickerPosition?: KickerPosition
    largeKicker?: boolean

    isLarge?: boolean
    verticalSpacing?: keyof ThemeMargins
    fullWidthOnMobile?: boolean
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ImageWidths['thewest'] | ImageWidths['perthnow'] | ImageWidths['sevennews']
    smallImage?: boolean
    fontScale?: FontScales
    isSponsoredCollection?: boolean
    textVerticalSpacing?: keyof ThemeMargins
    disableImageLazyLoad?: boolean

    timestamp?: TimestampType
    cardNumber: number
    innerRef?: React.RefObject<any>
    cardOrientation?: CardOrientationOptions
    type?: string // Type of card

    adUnitPath?: string
    willPlayVideoInline?: boolean

    item: MaybeLoaded<PublicationCardItem>
    getVideoQueue?: GetVideoQueue
}

export class InternalPortrait extends React.Component<Props & ThemeProps, PortraitState> {
    static displayName = 'InternalPortrait'

    state: PortraitState = {
        isPlaying: false,
    }

    linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!this.props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        if (
            this.props.willPlayVideoInline &&
            this.props.fixedRatio &&
            doesParticularRatioExist(this.props.fixedRatio, '16:9') &&
            e.nativeEvent.target instanceof HTMLElement &&
            isVideoClick(e.nativeEvent.target.className)
        ) {
            this.setState({ isPlaying: true })
            e.preventDefault()
            return
        } else {
            this.props.onEvent(
                createCardClickedEvent(
                    this.props.item.result,
                    'InternalPortrait',
                    'Portrait',
                    this.props.cardNumber
                )
            )
        }
    }

    render() {
        const {
            className,
            theme,
            hasBackground,
            hasSeparator,
            verticalSpacing,
            isLarge,
            fixedRatio,
            cardOrientation,
            fullWidthOnMobile,
        } = this.props

        // SWM-4731 When the page is sponsored, cards should never show the sponsored status
        const showSponsoredLabel =
            !this.props.item.loaded || this.props.isSponsoredCollection
                ? false
                : this.props.item.result.isSponsored

        const cardItem = this.props.item
        const getVideoQueue = !this.props.item.loaded ? undefined : this.props.getVideoQueue

        // determine if we are displaying an Opinion Card
        const isOpinionPublication =
            this.props.item.loaded && this.props.item.result.primaryTopic.id.indexOf('opinion') > -1
        const isWestOpinionCard = theme.kind === 'thewest' && isOpinionPublication
        const opinionMediaMode = isWestOpinionCard && this.props.mediaMode !== 'hidden'

        // TODO: Consider alternatives around adding breakpoints object to hideTeaser prop.
        const hideTeaser = themedValue(theme, {
            sevennews: this.props.hideTeaser,
            thewest:
                (this.props.hideTeaser && cardOrientation === undefined) ||
                (this.props.hideTeaser &&
                    cardOrientation &&
                    cardOrientation.maxBreakpoint !== 'sm'),
            perthnow: this.props.hideTeaser,
        })

        const mediaMode = opinionMediaMode ? 'edgeToEdge' : this.props.mediaMode

        const fontScale = themedValue(theme, {
            thewest: this.props.fontScale || 0.9,
            perthnow: this.props.fontScale,
            sevennews: this.props.fontScale || 1.2,
        }) as FontScales

        const LandscapePortraitCard =
            cardOrientation && cardOrientation.type === 'Landscape-Portrait'

        return (
            <StyledPortrait
                ref={this.props.innerRef}
                className={className}
                data-topic={
                    this.props.item.loaded ? this.props.item.result.primaryTopic.id : undefined
                }
                hasBackground={hasBackground}
                hasSeparator={hasSeparator}
                isLarge={isLarge}
                cardOrientation={cardOrientation}
                verticalSpacing={verticalSpacing}
                mediaMode={mediaMode}
                fullWidthOnMobile={fullWidthOnMobile}
            >
                <StyledLink
                    to={toLinkWithHint(this.props.item)}
                    onClick={this.linkClicked}
                    hasBackground={hasBackground}
                    isLarge={isLarge}
                    cardOrientation={cardOrientation}
                    hasPadding={this.props.hasPadding}
                    fullWidthOnMobile={fullWidthOnMobile}
                    isSponsored={showSponsoredLabel}
                >
                    {mediaMode !== 'hidden' && (
                        <StyledCardMedia
                            item={cardItem}
                            fixedRatio={
                                LandscapePortraitCard ? { default: '16:9', sm: '4:3' } : fixedRatio
                            }
                            hasBackground={hasBackground}
                            imageWidths={this.props.imageWidths}
                            onEvent={this.props.onEvent}
                            disableImageLazyLoad={this.props.disableImageLazyLoad}
                            hasGradientOverlay={isLarge || hasBackground}
                            isLarge={isLarge}
                            cardOrientation={cardOrientation}
                            getVideoQueue={getVideoQueue}
                            willPlayVideoInline={this.props.willPlayVideoInline}
                            adUnitPath={this.props.adUnitPath}
                            mediaMode={mediaMode}
                            fullWidthOnMobile={fullWidthOnMobile}
                        />
                    )}
                    <StyledCardText
                        item={cardItem}
                        hasBackground={hasBackground}
                        hideKicker={this.props.hideKicker || this.state.isPlaying}
                        hideTeaser={hideTeaser}
                        hideByline={this.props.hideByline}
                        largeKicker={this.props.largeKicker}
                        fontScale={fontScale}
                        onEvent={this.props.onEvent}
                        kickerPosition={this.props.kickerPosition}
                        verticalSpacing={this.props.textVerticalSpacing}
                        hasPadding={this.props.hasPadding}
                        isLarge={isLarge}
                        timestamp={this.props.timestamp}
                        cardOrientation={cardOrientation}
                        innerRef={undefined}
                        disableImageLazyLoad={undefined}
                        className={undefined}
                    />

                    {!this.props.hideByline &&
                        !(showSponsoredLabel && theme.kind === 'perthnow') && (
                            <StyledCardByline
                                item={cardItem}
                                onEvent={this.props.onEvent}
                                showProfile={isWestOpinionCard}
                                isEdgeToEdgeCard={isWestOpinionCard}
                                cardOrientation={cardOrientation}
                                mediaMode={this.props.mediaMode}
                            />
                        )}
                    {showSponsoredLabel && (
                        <StyledSponsoredLabel
                            source={
                                theme.kind === 'perthnow' && cardItem.loaded
                                    ? cardItem.result.byline
                                    : undefined
                            }
                            hasBackground={hasBackground}
                        />
                    )}
                </StyledLink>
            </StyledPortrait>
        )
    }
}

export const Portrait = withTheme(InternalPortrait)
Portrait.displayName = 'Portrait'
